import dayjs from "dayjs";
import { z } from "zod";
import { PaginationSchema } from "@/components/paginate/types";

// ============================================================================
// Query Keys
// ============================================================================

// Which cloud the Altimate deployment runs on. Comes from tenant-info's
// `deploymentCloud`; selects which variant of the connection form is shown.
export const DeploymentCloud = {
  AWS: "aws",
  AZURE: "azure",
} as const;
export type DeploymentCloudValue = (typeof DeploymentCloud)[keyof typeof DeploymentCloud];

export const DATABRICKS_QUERY_KEYS = {
  CONNECTION: "databricks-connection",
  SYNC_HISTORY: "databricks-sync-history",
  WORKSPACES: "databricks-connection-workspaces",
} as const;

// ============================================================================
// Routes
// ============================================================================

export const DATABRICKS_ROUTES = {
  list: "/settings/datastores",
} as const;

// ============================================================================
// Constants
// ============================================================================

// The form only ever sends OAUTH_M2M; PAT still round-trips from older
// stored connections, so the response schema keeps it.
export const DatabricksAuthType = {
  PAT: "pat",
  OAUTH_M2M: "oauth_m2m",
} as const;
export type DatabricksAuthTypeValue = (typeof DatabricksAuthType)[keyof typeof DatabricksAuthType];

// ============================================================================
// Shared Auth Schemas (internal building blocks)
// ============================================================================

const DatabricksPATAuthSchema = z.object({
  auth_type: z.literal(DatabricksAuthType.PAT),
  access_token: z.string().min(1, "Access token is required"),
});

const DatabricksOAuthM2MAuthSchema = z.object({
  auth_type: z.literal(DatabricksAuthType.OAUTH_M2M),
  client_id: z.string().min(1, "Client ID is required"),
  client_secret: z.string().min(1, "Client Secret is required"),
  // Present for Entra-managed service principals
  azure_tenant_id: z.string().min(1).optional(),
});

// ============================================================================
// API Response Schemas
// ============================================================================

export const DatabricksConnectionResponseSchema = z.object({
  id: z.number(),
  name: z.string(),
  description: z.string().nullable().optional(),
  server_hostname: z.string(),
  http_path: z.string(),
  auth_type: z.nativeEnum(DatabricksAuthType).default(DatabricksAuthType.PAT),
  azure_tenant_id: z.string().nullable().optional(),
  workspace_id: z.string().nullable().optional(),
  // Resolved live from ClickHouse. Null when the workspace hasn't been
  // ingested yet or CH is down — callers fall back to workspace_id.
  workspace_name: z.string().nullable().optional(),
  // Null (never []) on connections that haven't been re-validated since
  // catalog listing shipped. Null means "unknown", not "none".
  catalogs: z.array(z.string()).nullable().optional(),
  metastore_id: z.string().nullable().optional(),
  // --- consolidated onboarding fields (secrets deliberately absent) ---
  databricks_account_id: z.string().nullable().optional(),
  workspace_url: z.string().nullable().optional(),
  sql_warehouse_id: z.string().nullable().optional(),
  system_catalog_scope: z.string().nullable().optional(),
  system_catalog_schemas: z.string().nullable().optional(),
  workspace_resource_id: z.string().nullable().optional(),
  azure_region: z.string().nullable().optional(),
  storage_account_resource_id: z.string().nullable().optional(),
  storage_account_fqdn: z.string().nullable().optional(),
  log_tenant_id: z.string().nullable().optional(),
  log_client_id: z.string().nullable().optional(),
  storage_account: z.string().nullable().optional(),
  container: z.string().nullable().optional(),
  log_path: z.string().nullable().optional(),
  // The secret itself is never returned — this only says one is on file, so the
  // form can render "configured" without it.
  has_log_client_secret: z.boolean().optional().default(false),
  s3_role_arn: z.string().nullable().optional(),
  // s3_external_id is the STS handshake value — semi-sensitive, the BE
  // deliberately never echoes it back on list/get responses.
  s3_location: z.string().nullable().optional(),
  is_valid: z.boolean().optional(),
  last_validated_at: z.string().nullable().optional(),
  // BE returns these as non-nullable datetimes — tighten the FE schema
  // so a missing field surfaces as a parse error instead of silently
  // becoming `null` in callers (PR #2155 review N15).
  created_at: z.string(),
  last_modified_at: z.string(),
  // Nullable: rows created by onboarding scripts/recipes predate the
  // connections API and carry no user attribution.
  created_by: z.number().nullable(),
  last_modified_by: z.number().nullable(),
});
export type DatabricksConnectionResponse = z.infer<typeof DatabricksConnectionResponseSchema>;

/** One workspace under the connection's Databricks account. */
export const DatabricksWorkspaceOptionSchema = z.object({
  workspace_id: z.string(),
  workspace_name: z.string(),
  workspace_url: z.string().nullable().optional(),
  status: z.string().nullable().optional(),
});
export const DatabricksWorkspacesResponseSchema = z.array(DatabricksWorkspaceOptionSchema);
export type DatabricksWorkspacesResponse = z.infer<typeof DatabricksWorkspacesResponseSchema>;

export const DatabricksConnectionTestResponseSchema = z.object({
  ok: z.boolean(),
  message: z.string().nullable().optional(),
  workspace_id: z.string().nullable().optional(),
  catalogs: z.array(z.string()).nullable().optional(),
  current_catalog: z.string().nullable().optional(),
  metastore_id: z.string().nullable().optional(),
  unreadable_system_tables: z.array(z.string()).nullable().optional(),
  system_access_warning: z.string().nullable().optional(),
});
export type DatabricksConnectionTestResponse = z.infer<
  typeof DatabricksConnectionTestResponseSchema
>;

// ============================================================================
// API Request Schemas
// ============================================================================

// The BE derives server_hostname from workspace_url and http_path from
// sql_warehouse_id in a mode="before" validator, so the form sends the
// customer-facing pair and never the derived keys.
const DatabricksConnectionBaseSchema = z.object({
  name: z.string().min(1, "Name is required").max(64),
  description: z.string().max(512).optional(),

  // --- Setup (both clouds) ---
  databricks_account_id: z.string().max(64).optional(),
  workspace_url: z.string().max(256).optional(),
  sql_warehouse_id: z.string().max(64).optional(),
  system_catalog_scope: z.enum(["all", "specific"]).optional(),
  system_catalog_schemas: z.string().max(1024).optional(),

  // --- Private Link (Azure only; AWS uses IP allowlisting) ---
  workspace_resource_id: z.string().max(512).optional(),
  azure_region: z.string().max(64).optional(),
  // The BE rejects half this pair — they travel together.
  storage_account_resource_id: z.string().max(512).optional(),
  storage_account_fqdn: z.string().max(256).optional(),

  // --- Cluster log access, AWS (S3 cross-account) ---
  s3_role_arn: z.string().optional(),
  s3_external_id: z.string().max(1224).optional(),
  // Widened BE-side to accept abfss:// too, so the field is shared.
  s3_location: z.string().optional(),

  // --- Cluster log access, Azure (ADLS) ---
  // A SEPARATE service principal from Setup (`altimate-log-reader`). The BE
  // rejects a partial block, so the form must send all six or none.
  log_tenant_id: z.string().max(64).optional(),
  log_client_id: z.string().max(128).optional(),
  log_client_secret: z.string().max(1024).optional(),
  storage_account: z.string().max(128).optional(),
  container: z.string().max(128).optional(),
  log_path: z.string().max(512).optional(),
});

export const DatabricksConnectionCreateRequestSchema = z.discriminatedUnion("auth_type", [
  DatabricksConnectionBaseSchema.merge(DatabricksPATAuthSchema),
  DatabricksConnectionBaseSchema.merge(DatabricksOAuthM2MAuthSchema),
]);
export type DatabricksConnectionCreateRequest = z.infer<
  typeof DatabricksConnectionCreateRequestSchema
>;

// Test carries the same derivation mixin as create, so it takes the
// customer-facing pair too.
const DatabricksTestBaseSchema = z.object({
  workspace_url: z.string().min(1, "Workspace URL is required"),
  sql_warehouse_id: z.string().min(1, "SQL warehouse ID is required"),
});

export const DatabricksConnectionTestRequestSchema = z.discriminatedUnion("auth_type", [
  DatabricksTestBaseSchema.merge(DatabricksPATAuthSchema),
  DatabricksTestBaseSchema.merge(DatabricksOAuthM2MAuthSchema),
]);
export type DatabricksConnectionTestRequest = z.infer<typeof DatabricksConnectionTestRequestSchema>;

// ============================================================================
// Sync History
// ============================================================================

// The backend's full domain is these three — it 422s on anything else. There is
// deliberately no spark_logs: Spark log ingestion records under the `spark`
// provider, and the feed is narrowed to `databricks_loader_*`, so the value
// could never be produced or matched.
export const DatabricksSyncCategory = {
  SYSTEM_TABLES: "system_tables",
  CLOUD_COST: "cloud_cost",
  LINEAGE: "lineage",
} as const;
export type DatabricksSyncCategoryValue =
  (typeof DatabricksSyncCategory)[keyof typeof DatabricksSyncCategory];

export const DATABRICKS_SYNC_CATEGORY_LABELS: Record<DatabricksSyncCategoryValue, string> = {
  [DatabricksSyncCategory.SYSTEM_TABLES]: "System tables",
  [DatabricksSyncCategory.CLOUD_COST]: "Cloud cost",
  [DatabricksSyncCategory.LINEAGE]: "Lineage",
};

// Every category the backend accepts is offered — the domain is closed and all
// three are reachable, so nothing needs hiding.
export const DATABRICKS_SYNC_CATEGORY_FILTER_OPTIONS = Object.keys(
  DATABRICKS_SYNC_CATEGORY_LABELS
) as DatabricksSyncCategoryValue[];

// The backend's actual status domain — it 422s on anything else. These are
// pipeline stages, not a success/running/failed triple: a run walks
// started → pending_ingestion → fetched → *_scheduled → *_processed →
// completed, and can fail at the ingestion, Airflow or Celery step.
export const DatabricksSyncStatus = {
  STARTED: "started",
  PENDING_INGESTION: "pending_ingestion",
  INGESTION_FAILED: "ingestion_failed",
  FETCHED: "fetched",
  AIRFLOW_SCHEDULED: "airflow_scheduled",
  AIRFLOW_FAILED: "airflow_failed",
  AIRFLOW_PROCESSED: "airflow_processed",
  CELERY_SCHEDULED: "celery_scheduled",
  CELERY_FAILED: "celery_failed",
  COMPLETED: "completed",
} as const;
export type DatabricksSyncStatusValue =
  (typeof DatabricksSyncStatus)[keyof typeof DatabricksSyncStatus];

/**
 * The raw stages are internal pipeline names — "Airflow scheduled" and
 * "Celery failed" mean nothing to a Databricks customer reading an audit
 * table. So the UI shows the OUTCOME, and the raw stage is exposed only as
 * supporting detail (a tooltip) for support work.
 */
export const DatabricksSyncOutcome = {
  COMPLETED: "completed",
  IN_PROGRESS: "in_progress",
  FAILED: "failed",
} as const;
export type DatabricksSyncOutcomeValue =
  (typeof DatabricksSyncOutcome)[keyof typeof DatabricksSyncOutcome];

export const DATABRICKS_SYNC_OUTCOME_LABELS: Record<DatabricksSyncOutcomeValue, string> = {
  [DatabricksSyncOutcome.COMPLETED]: "Completed",
  [DatabricksSyncOutcome.IN_PROGRESS]: "In progress",
  [DatabricksSyncOutcome.FAILED]: "Failed",
};

// Every raw status maps into exactly one outcome. Filtering by an outcome
// sends all of its statuses, since the API only accepts raw values.
export const DATABRICKS_SYNC_OUTCOME_STATUSES: Record<DatabricksSyncOutcomeValue, string[]> = {
  [DatabricksSyncOutcome.COMPLETED]: [DatabricksSyncStatus.COMPLETED],
  [DatabricksSyncOutcome.IN_PROGRESS]: [
    DatabricksSyncStatus.STARTED,
    DatabricksSyncStatus.PENDING_INGESTION,
    DatabricksSyncStatus.FETCHED,
    DatabricksSyncStatus.AIRFLOW_SCHEDULED,
    DatabricksSyncStatus.AIRFLOW_PROCESSED,
    DatabricksSyncStatus.CELERY_SCHEDULED,
  ],
  [DatabricksSyncOutcome.FAILED]: [
    DatabricksSyncStatus.INGESTION_FAILED,
    DatabricksSyncStatus.AIRFLOW_FAILED,
    DatabricksSyncStatus.CELERY_FAILED,
  ],
};

/** Outcome for a raw status, or undefined for a status the FE doesn't know. */
export const databricksSyncOutcome = (status: string): DatabricksSyncOutcomeValue | undefined =>
  (Object.keys(DATABRICKS_SYNC_OUTCOME_STATUSES) as DatabricksSyncOutcomeValue[]).find(outcome =>
    DATABRICKS_SYNC_OUTCOME_STATUSES[outcome].includes(status)
  );

/**
 * Human-readable stage, for the tooltip behind the outcome tag. Derived from
 * the raw value so a stage added server-side still reads sensibly instead of
 * needing an FE release: "celery_failed" → "Celery failed".
 */
export const databricksSyncStageLabel = (status: string): string => {
  const spaced = status.replace(/_/g, " ").trim();
  if (spaced === "") return status;
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

export const DatabricksSyncRunSchema = z.object({
  // An integer on the wire — a string here fails the whole response parse.
  batch_id: z.number(),
  dataset_type: z.string(),
  // Derived server-side, so new loaders show up without an FE change.
  // Altimate-computed marts carry a " (derived)" suffix. Render this,
  // never dataset_type.
  label: z.string(),
  category: z.string(),
  status: z.string(),
  // Environment tag the run recorded ("main", "aws", …). A tenant that has been
  // through a deployment rename has runs from both with the same dataset and
  // window, so this is what keeps them from looking like duplicate rows.
  deployment: z.string().nullable().optional(),
  // The business window the run covers — NOT when it ran. For "when did
  // this last sync", use last_updated_on.
  fetch_start_datetime: z.string().nullable().optional(),
  fetch_end_datetime: z.string().nullable().optional(),
  last_updated_on: z.string().nullable().optional(),
});
export type DatabricksSyncRun = z.infer<typeof DatabricksSyncRunSchema>;

export const DatabricksSyncHistoryResponseSchema = z.object({
  ...PaginationSchema.shape,
  items: z.array(DatabricksSyncRunSchema),
});
export type DatabricksSyncHistoryResponse = z.infer<typeof DatabricksSyncHistoryResponseSchema>;

export interface DatabricksSyncHistoryParams {
  status?: string[];
  category?: string[];
  page?: number;
  size?: number;
}

// Display label for a stored connection's auth method. An Entra-managed
// service principal is stored as oauth_m2m with azure_tenant_id set, so the
// label derives from both fields.
export const databricksAuthTypeLabel = (connection: {
  auth_type: DatabricksAuthTypeValue;
  azure_tenant_id?: string | null;
}): string => {
  if (connection.auth_type === DatabricksAuthType.OAUTH_M2M) {
    return connection.azure_tenant_id
      ? "Azure service principal (Entra ID)"
      : "OAuth M2M (service principal)";
  }
  return "Personal access token";
};

// "/sql/1.0/warehouses/8a1bd94f" → "…/warehouses/8a1bd94f"; the prefix is
// identical on every connection, the warehouse id is the differentiator.
export const warehousePathLabel = (httpPath: string): string => {
  const marker = "/warehouses/";
  const idx = httpPath.lastIndexOf(marker);
  if (idx === -1) return httpPath;
  return `…${marker}${httpPath.slice(idx + marker.length)}`;
};

const EM_DASH = "—";

// The one datetime rendering for this feature — the card footer, the detail
// page and sync history all show the same instants, so they must not drift
// into three different formats.
export const formatDateTime = (value: string | null | undefined): string => {
  if (value === null || value === undefined || value === "") return EM_DASH;
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("D MMM YYYY, h:mm A") : EM_DASH;
};

// Shown on the disabled Test connection control — the only explanation a user
// without DATASTORE_EDIT gets, on both the card and the detail page.
export const TEST_PERMISSION_HINT = "Insufficient permissions for testing the connection";

export interface DatabricksConnectionFact {
  label: string;
  value: string;
  /** Identifier-like values read better in a monospace face. */
  mono?: boolean;
}
